import { Typography } from '@mui/material';
import {
  athleteDisplayName,
  AthletePreview,
  Metric,
  WorkoutStatus,
} from '@strength/domain';
import {
  ExerciseBenchmark,
  getInputDisplay,
  KpiMetricResolution,
  MaxConversion,
  SetMetricMutationData,
  WorkoutExercise,
  WorkoutExerciseMetric,
  WorkoutSet,
} from '@strength/domain-workout';
import { getNormalizedSortOrders, sortByOrder } from '@strength/utils';
import { MdOpenInNew } from '@teamworksdev/icons';
import { atom } from 'jotai';
import { useState } from 'react';
import { useViewer } from '../../../context/viewer';
import { WorkoutStatusBadge } from '../../../components/badge/workout-status-badge';
import { CustomSlot } from '../../../components/sheet/slots';
import { cn } from '../../../components/utils';
import { ActualWorkoutLayerMutationFns } from '../actuals-mutations';
import { getWorkoutStatusForAthleteWorkout } from '../actuals-workout-status';
import { ActualsSetFilter } from '../set-select';
import { DeltaIndicator } from '../tablet/components/tablet-inputs/DeltaIndicator';
import { AthleteWorkoutExercises } from '../types';
import { detectActualsUnit, shouldDisplaySet } from '../utils';
import { MetricsSlot } from './../../../components/sheet/slots/metrics-slot';
import { ActualSetMetricCellContent } from './actual-sets';
import { ActualSlot } from './types';

/**
 * SC-4845: the athlete layers that feed a row set arrive in query order, so the
 * athletes listed under an exercise were effectively in a random order. Sort
 * them by the athlete's last name, then first name, so every exercise lists the
 * selected athletes the same, predictable way.
 */
const sortAthleteWorkoutExercisesByLastName = (
  athleteWorkoutExercises: AthleteWorkoutExercises[],
  athleteSelection: AthletePreview[],
): AthleteWorkoutExercises[] => {
  const nameByProfileId = new Map(
    athleteSelection.map((athlete) => [
      athlete.id,
      { givenName: athlete.givenName, familyName: athlete.familyName },
    ]),
  );

  return [...athleteWorkoutExercises].sort((a, b) => {
    const aName = nameByProfileId.get(a.context.profileId);
    const bName = nameByProfileId.get(b.context.profileId);
    return (
      (aName?.familyName ?? '').localeCompare(bName?.familyName ?? '') ||
      (aName?.givenName ?? '').localeCompare(bName?.givenName ?? '')
    );
  });
};

export const getSetSlotsForAthletes = (
  athleteWorkoutExercises: AthleteWorkoutExercises[],
  athleteExerciseMetrics: WorkoutExerciseMetric[],
  athleteSelection: AthletePreview[],
  mutationFns: ActualWorkoutLayerMutationFns,
  setFilter: ActualsSetFilter,
  y: number,
  getMetric: (id: string) => Metric,
  getUnitsForMetric: (id: string, forActuals?: boolean) => string[],
  exerciseBenchmarks: ExerciseBenchmark[],
  conversions: MaxConversion[],
  trainingMaxRatio: number,
  defaultBenchmarkGridId: string,
  // KPI benchmark resolutions keyed by profileId (SC-4041), so a `%` on a
  // KPI-mapped column resolves to each athlete's target.
  kpiBenchmarksByProfile: Record<string, Record<string, KpiMetricResolution>>,
  // Actuals Log revoked: every actuals cell renders read-only (SC-4378).
  readOnly: boolean,
): { slots: ActualSlot[]; rowCount: number } => {
  let curY = y;
  let rowCount = 0;
  const slots: ActualSlot[] = [];

  // For each athlete, if they are assigned this exercise display all of their sets
  sortAthleteWorkoutExercisesByLastName(
    athleteWorkoutExercises,
    athleteSelection,
  ).forEach(({ context, workoutExercise, originalExercise }) => {
    const athlete = athleteSelection.find(
      (athlete) => athlete.id === context.profileId,
    );
    if (!athlete) {
      return;
    }

    const onEditSetMetric = (
      workoutSetId: string,
      value: SetMetricMutationData,
    ) => {
      return mutationFns.onEditSetMetric(
        context,
        workoutExercise.workoutId,
        workoutSetId,
        value,
      );
    };

    const setSortOrders = getNormalizedSortOrders(workoutExercise.sets);
    const setsForDisplay = workoutExercise.sets.filter((s, index) =>
      shouldDisplaySet(s, index, setFilter, workoutExercise.sets.length),
    );
    setsForDisplay.forEach((s, index) => {
      rowCount++;
      slots.push(
        ...getAthleteSetSlots({
          showAthleteName: index === 0,
          libraryExerciseId: workoutExercise.libraryExerciseId,
          athlete,
          athleteWorkoutStatus: context.workoutStatus,
          athleteExerciseMetrics,
          set: s,
          setMetrics: workoutExercise.metrics.filter((m) =>
            athleteExerciseMetrics.some((am) => am.metricId === m.metricId),
          ),
          originalExercise,
          y: curY++,
          onEditSetMetric,
          getMetric,
          getUnitsForMetric,
          sortOrder: setSortOrders.get(s.sortOrder) ?? 1,
          numSets: setsForDisplay.length,
          blockId: context.blockId,
          conversions,
          trainingMaxRatio,
          exerciseBenchmarks,
          defaultBenchmarkGridId,
          kpiBenchmarks: kpiBenchmarksByProfile[context.profileId] ?? {},
          readOnly,
        }),
      );
    });
  });

  return { slots, rowCount };
};

const baseStyle = 'p-1.5 text-sm';

const inputAtom = atom<string>('');

const getAthleteSetSlots = ({
  showAthleteName,
  libraryExerciseId,
  athlete,
  athleteWorkoutStatus,
  athleteExerciseMetrics,
  set,
  setMetrics,
  originalExercise,
  onEditSetMetric,
  y,
  getMetric,
  getUnitsForMetric,
  sortOrder,
  numSets,
  blockId,
  conversions,
  trainingMaxRatio,
  exerciseBenchmarks,
  defaultBenchmarkGridId,
  kpiBenchmarks,
  readOnly,
}: {
  showAthleteName: boolean;
  libraryExerciseId: string;
  athlete: AthletePreview;
  athleteWorkoutStatus: WorkoutStatus;
  athleteExerciseMetrics: WorkoutExerciseMetric[];
  set: WorkoutSet;
  setMetrics: WorkoutExerciseMetric[];
  originalExercise?: WorkoutExercise;
  onEditSetMetric: (workoutSetId: string, data: SetMetricMutationData) => void;
  y: number;
  getMetric: (id: string) => Metric;
  getUnitsForMetric: (id: string, forActuals?: boolean) => string[];
  sortOrder: number;
  numSets: number;
  blockId: string;
  conversions: MaxConversion[];
  trainingMaxRatio: number;
  exerciseBenchmarks: ExerciseBenchmark[];
  defaultBenchmarkGridId: string;
  kpiBenchmarks: Record<string, KpiMetricResolution>;
  readOnly: boolean;
}): ActualSlot[] => {
  // This shouldn't happen, but the type of originalExercise is optional, so this just
  // makes the rest of the code cleaner
  if (!originalExercise) {
    throw new Error('Original exercise is required to render actuals');
  }

  let curX = 0;
  const athleteNameSlot: ActualSlot[] = [];
  if (showAthleteName) {
    athleteNameSlot.push(
      new CustomSlot(
        { x: curX, y },
        1,
        numSets,
        false,
        () => (
          <AthleteWorkoutSlotContent
            athlete={athlete}
            workoutStatus={athleteWorkoutStatus}
            blockId={blockId}
            workoutId={originalExercise.workoutId}
          />
        ),
        {
          className: cn(
            baseStyle,
            '!items-start sticky left-0 z-30 bg-white w-full w-[180px]',
          ),
        },
      ),
    );
  }
  // add 1 column for athlete name
  curX += 1;

  const setSlot: ActualSlot = new CustomSlot(
    { x: curX++, y },
    1,
    1,
    true,
    () => <Typography variant="body2">{sortOrder}</Typography>,
    {
      className: cn(
        baseStyle,
        'sticky left-[180px] z-30 bg-white opacity-100 w-full w-[80px]',
      ),
    },
  );

  const filteredSet = {
    ...set,
    metrics: set.metrics.filter((m) =>
      setMetrics.find((sm) => sm.metricId === m.metricId),
    ),
  };

  const originalSet = originalExercise?.sets.find(
    (set) => set.workoutSetId === filteredSet.workoutSetId,
  );

  // filter out metrics that aren't chosen for display
  const prescribedMetrics = originalExercise.metrics.filter((m) =>
    athleteExerciseMetrics.some((am) => am.metricId === m.metricId),
  );

  const prescribedSlots: ActualSlot[] = prescribedMetrics.map((metric) => {
    const metricInSet = originalSet?.metrics.find(
      (m) => m.metricId === metric.metricId,
    );

    const display = getInputDisplay({
      metric: getMetric(metric.metricId),
      prescribedValue: metricInSet?.input,
      benchmarkData: {
        libraryExerciseId: originalExercise?.libraryExerciseId,
        profileId: athlete.id,
        benchmarkGridId: metric.benchmarkGridId,
        metricId: metric.metricId,
        applyBenchmarkData: {
          defaultBenchmarkGridId,
          currentExerciseBenchmarks: exerciseBenchmarks,
          maxConversions: conversions,
          trainingMaxRatio,
          kpiBenchmarks,
        },
      },
    });
    let unit = display.unit ?? '';
    if (String(display.placeholder).includes('%')) {
      unit = '';
    }
    const value =
      metric.metricType === 'tempo' ? display.value : display.placeholder;
    return new CustomSlot(
      { x: curX++, y },
      1,
      1,
      true,
      () => <Typography variant="body2">{`${value}${unit}`}</Typography>,
      { className: baseStyle, backgroundClassName: 'bg-gray-50' },
    );
  });

  const metricActualSlot: ActualSlot[] = [
    new MetricsSlot(
      readOnly,
      (metric) => {
        // disable metric if it is not collectable or metric not in set
        const orgMetric = getMetric(metric.metricId);
        const metricInSet = setMetrics.find(
          (m) => m.metricId === metric.metricId,
        );
        return !orgMetric?.isCollectable || !metricInSet;
      },
      { x: curX, y },
      sortByOrder(athleteExerciseMetrics),
      [filteredSet],
      (edit, set) => {
        const currentValue = set.metrics.find(
          (m) => m.metricId === edit.metricId,
        );

        const prescribedMetric = originalSet?.metrics.find(
          (m) => m.metricId === edit.metricId,
        );

        let input = edit.input.replace(/\s/g, '');
        if (input !== '' && Number.isFinite(+input)) {
          // A valid number with no unit was entered
          const metric = getMetric(edit.metricId);
          const units = getUnitsForMetric(metric.id, true);

          input += detectActualsUnit(prescribedMetric?.input, metric, units);
        }

        if (currentValue?.input === input) {
          return;
        }
        onEditSetMetric(set.workoutSetId, {
          metricId: edit.metricId,
          input,
        });
      },
      inputAtom,
      (props) => {
        const metric = props.metric;
        const prescribedMetric = originalSet?.metrics.find(
          (m) => m.metricId === metric.metricId,
        );
        const actualMetric = set.metrics.find(
          (m) => m.metricId === metric.metricId,
        );

        const inputDisplay = getInputDisplay({
          metric: getMetric(metric.metricId),
          prescribedValue: prescribedMetric?.input,
          actualValue: actualMetric?.input,
          benchmarkData: props.context.applyBenchmarkData
            ? {
                libraryExerciseId,
                profileId: athlete.id,
                benchmarkGridId: metric.benchmarkGridId,
                metricId: metric.metricId,
                applyBenchmarkData: props.context.applyBenchmarkData,
              }
            : undefined,
        });
        let prescribedValue =
          metric.metricType === 'tempo'
            ? inputDisplay.value
            : inputDisplay.placeholder;
        let actualValue = inputDisplay.value;
        if (inputDisplay.delimiter) {
          prescribedValue = String(inputDisplay.placeholder)
            .split(inputDisplay.delimiter)
            .reduce((sum, val) => sum + Number(val), 0);
          actualValue = String(inputDisplay.value)
            .split(inputDisplay.delimiter)
            .reduce((sum, val) => sum + Number(val), 0);
        }

        // The calculateDelta function expects the original input (value + unit)
        let prescribedDisplay = String(prescribedValue);
        let actualDisplay = String(actualValue);
        if (!prescribedDisplay.endsWith('%')) {
          prescribedDisplay += inputDisplay.unit ?? '';
        }
        if (!actualDisplay.endsWith('%')) {
          actualDisplay += inputDisplay.unit ?? '';
        }

        const badge = (
          <DeltaIndicator
            metric={getMetric(metric.metricId)}
            prescribed={prescribedDisplay}
            actual={actualDisplay}
            benchmarkData={{
              libraryExerciseId: originalExercise.libraryExerciseId,
              profileId: athlete.id,
              benchmarkGridId: metric.benchmarkGridId,
              metricId: metric.metricId,
              applyBenchmarkData: {
                defaultBenchmarkGridId,
                currentExerciseBenchmarks: exerciseBenchmarks,
                maxConversions: conversions,
                trainingMaxRatio,
                kpiBenchmarks,
              },
            }}
          />
        );

        return (
          <ActualSetMetricCellContent
            {...props}
            hideCollectionFlag={true}
            originalSet={originalSet}
            badge={badge}
            inputDisplay={inputDisplay}
          />
        );
      },
      {
        workoutErrors: null,
        applyBenchmarkData: {
          currentExerciseBenchmarks: exerciseBenchmarks,
          maxConversions: conversions,
          trainingMaxRatio,
          profileId: athlete.id,
          libraryExerciseId,
          defaultBenchmarkGridId,
          kpiBenchmarks,
        },
        libraryExerciseId,
      },
      1,
      0,
      getMetric,
      {
        hideHeaders: true,
        // Athletes record what they did, so the cell's unit suggestions must not
        // offer the prescription-only `%` (SC-4161).
        forActuals: true,
      },
    ),
  ];
  curX += athleteExerciseMetrics.length;

  // Add single flexible blank slot
  const blankSlots: ActualSlot[] = [
    new CustomSlot(
      { x: 2 + athleteExerciseMetrics.length * 2, y },
      1,
      1,
      true,
      () => <></>,
      {
        className: 'bg-transparent pointer-events-none p-0 min-w-0',
      },
    ),
  ];

  return [
    ...athleteNameSlot,
    ...prescribedSlots,
    setSlot,
    ...metricActualSlot,
    ...blankSlots,
  ];
};

interface AthleteWorkoutSlotContentProps {
  athlete: AthletePreview;
  workoutStatus: WorkoutStatus;
  blockId: string;
  workoutId: string;
}

function AthleteWorkoutSlotContent({
  athlete,
  workoutStatus,
  blockId,
  workoutId,
}: AthleteWorkoutSlotContentProps) {
  const [hover, setHover] = useState(false);
  const {
    team: { selectedTeam: teamId },
  } = useViewer();
  return (
    <div
      className="flex items-start gap-2 w-full justify-between h-full"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Typography variant="body2" className="truncate flex-1 min-w-0">
        {athleteDisplayName(athlete)}
      </Typography>
      {workoutStatus !== 'planned' && (
        <WorkoutStatusBadge
          status={getWorkoutStatusForAthleteWorkout(workoutStatus)}
          short={true}
        />
      )}
      {hover && (
        <a
          href={`/${teamId}/block/${blockId}?athleteId=${athlete.id}&workoutId=${workoutId}&showActuals=true`}
          target="_blank"
        >
          <MdOpenInNew />
        </a>
      )}
    </div>
  );
}
